// =====================================================
// SERVICE: PRATICIENS SANTÉ PSY ONLINE
// Date: 2025-10-02
// Description: Gestion des praticiens (psychologues, psychiatres, etc.)
// =====================================================

import { supabase } from '../lib/supabase';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../hooks/useDomainQuery';
import type { PsyPractitioner, PractitionerFilters } from '../types/psy.types';

// =====================================================
// RÉCUPÉRATION PRATICIENS
// =====================================================

/**
 * Récupère tous les praticiens approuvés avec filtres optionnels
 */
export const getPractitioners = async (filters?: PractitionerFilters) => {
  let query = supabase
    .from('psy_practitioners')
    .select('*');

  // Filtrer par statut seulement si pas admin (pour page publique)
  // Si includeAll n'est pas spécifié, on filtre sur approved
  if (!filters?.includeAll) {
    query = query.eq('verification_status', 'approved');
  }

  // Appliquer filtrage multi-tenant
  query = applyDomainFilterToQuery(query);

  // Filtres optionnels
  if (filters?.specialties?.length) {
    query = query.contains('specialties', filters.specialties);
  }

  if (filters?.languages?.length) {
    query = query.contains('languages', filters.languages);
  }

  if (filters?.city) {
    query = query.eq('city', filters.city);
  }

  if (filters?.minRate !== undefined) {
    query = query.gte('base_rate_per_session', filters.minRate);
  }

  if (filters?.maxRate !== undefined) {
    query = query.lte('base_rate_per_session', filters.maxRate);
  }

  if (filters?.acceptsNewPatients !== undefined) {
    query = query.eq('accepts_new_patients', filters.acceptsNewPatients);
  }

  if (filters?.consultationModes?.length) {
    query = query.contains('consultation_modes', filters.consultationModes);
  }

  // Tri par note moyenne décroissante
  query = query.order('average_rating', { ascending: false });

  const { data, error } = await query;

  if (error) throw error;
  return data as PsyPractitioner[];
};

/**
 * Récupère un praticien par son ID
 */
export const getPractitionerById = async (id: string) => {
  let query = supabase
    .from('psy_practitioners')
    .select('*')
    .eq('id', id)
    .single();

  query = applyDomainFilterToQuery(query);

  const { data, error } = await query;
  if (error) throw error;
  return data as PsyPractitioner;
};

/**
 * Récupère un praticien par user_id (pour dashboard praticien)
 */
export const getPractitionerByUserId = async (userId: string) => {
  let query = supabase
    .from('psy_practitioners')
    .select('*')
    .eq('user_id', userId)
    .single();

  query = applyDomainFilterToQuery(query);

  const { data, error } = await query;
  if (error) {
    if (error.code === 'PGRST116') return null; // Pas trouvé
    throw error;
  }
  return data as PsyPractitioner;
};

// =====================================================
// CRÉATION & MODIFICATION
// =====================================================

/**
 * Crée un nouveau praticien (onboarding)
 */
export const createPractitioner = async (practitionerData: Partial<PsyPractitioner>) => {
  const { domain, tenantId } = getDomainFilterParams();

  const { data, error } = await supabase
    .from('psy_practitioners')
    .insert({
      ...practitionerData,
      domain,
      tenant_id: tenantId,
      verification_status: 'pending',
      total_consultations: 0,
      average_rating: 0,
      response_rate_percentage: 100,
    })
    .select()
    .single();

  if (error) throw error;
  return data as PsyPractitioner;
};

/**
 * Met à jour un praticien
 */
export const updatePractitioner = async (
  id: string,
  updates: Partial<PsyPractitioner>
) => {
  const { data, error } = await supabase
    .from('psy_practitioners')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as PsyPractitioner;
};

/**
 * Met à jour le statut de vérification (admin)
 */
export const updateVerificationStatus = async (
  id: string,
  status: 'approved' | 'rejected' | 'suspended',
  verifiedBy?: string,
  notes?: string,
  rejectionReason?: string
) => {
  const updates: Partial<PsyPractitioner> = {
    verification_status: status,
    verified_at: new Date().toISOString(),
    verification_notes: notes,
  };

  if (verifiedBy) {
    updates.verified_by = verifiedBy;
  }

  if (status === 'rejected' && rejectionReason) {
    updates.rejection_reason = rejectionReason;
  }

  return updatePractitioner(id, updates);
};

/**
 * Alias pour updateVerificationStatus (utilisé par les pages admin)
 */
export const updatePractitionerStatus = async (
  id: string,
  status: 'approved' | 'rejected' | 'suspended',
  rejectionReason?: string
) => {
  return updateVerificationStatus(id, status, undefined, undefined, rejectionReason);
};

// =====================================================
// STATISTIQUES
// =====================================================

/**
 * Incrémente le nombre de consultations
 */
export const incrementConsultationCount = async (practitionerId: string) => {
  const { error } = await supabase.rpc('increment_practitioner_consultations', {
    practitioner_id: practitionerId,
  });

  if (error) {
    // Fallback si la fonction RPC n'existe pas encore
    const practitioner = await getPractitionerById(practitionerId);
    await updatePractitioner(practitionerId, {
      total_consultations: practitioner.total_consultations + 1,
    });
  }
};

/**
 * Met à jour la note moyenne
 */
export const updateAverageRating = async (
  practitionerId: string,
  newRating: number
) => {
  const practitioner = await getPractitionerById(practitionerId);
  const currentTotal = practitioner.average_rating * practitioner.total_consultations;
  const newAverage =
    (currentTotal + newRating) / (practitioner.total_consultations + 1);

  await updatePractitioner(practitionerId, {
    average_rating: Math.round(newAverage * 100) / 100, // 2 décimales
  });
};

// =====================================================
// RECHERCHE AVANCÉE
// =====================================================

/**
 * Recherche praticiens par nom ou spécialité
 */
export const searchPractitioners = async (searchTerm: string) => {
  let query = supabase
    .from('psy_practitioners')
    .select('*')
    .eq('verification_status', 'approved');

  query = applyDomainFilterToQuery(query);

  // Recherche dans nom, bio, spécialités
  query = query.or(
    `full_name.ilike.%${searchTerm}%,bio.ilike.%${searchTerm}%,specialties.cs.{${searchTerm}}`
  );

  const { data, error } = await query;
  if (error) throw error;
  return data as PsyPractitioner[];
};

/**
 * Récupère les praticiens disponibles aujourd'hui
 */
export const getPractitionersAvailableToday = async () => {
  // TODO: Implémenter la logique avec psy_availabilities
  // Pour l'instant, retourne tous les praticiens qui acceptent de nouveaux patients
  return getPractitioners({ acceptsNewPatients: true });
};

// =====================================================
// ADMIN
// =====================================================

/**
 * Récupère tous les praticiens (admin) avec tous les statuts
 */
export const adminGetAllPractitioners = async (status?: string) => {
  let query = supabase.from('psy_practitioners').select('*');

  query = applyDomainFilterToQuery(query);

  if (status) {
    query = query.eq('verification_status', status);
  }

  query = query.order('created_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return data as PsyPractitioner[];
};

/**
 * Récupère les praticiens en attente de vérification
 */
export const getPendingPractitioners = async () => {
  return adminGetAllPractitioners('pending');
};

// =====================================================
// UPLOAD DOCUMENTS
// =====================================================

/**
 * Upload un document praticien (diplôme, licence, photo)
 */
export const uploadPractitionerDocument = async (
  practitionerId: string,
  file: File,
  documentType: 'diploma' | 'license' | 'photo' | 'video'
): Promise<string> => {
  const { domain, tenantId } = getDomainFilterParams();
  const fileExt = file.name.split('.').pop();
  const fileName = `${tenantId}/${practitionerId}/${documentType}_${Date.now()}.${fileExt}`;

  // Upload vers bucket dédié
  const { data, error } = await supabase.storage
    .from('psy_practitioner_docs')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) throw error;

  // Générer URL signée (valide 1 an)
  const { data: urlData } = await supabase.storage
    .from('psy_practitioner_docs')
    .createSignedUrl(fileName, 31536000); // 365 jours

  if (!urlData) throw new Error('Impossible de générer l\'URL du document');

  return urlData.signedUrl;
};

/**
 * Supprime un document praticien
 */
export const deletePractitionerDocument = async (fileUrl: string) => {
  // Extraire le chemin du fichier depuis l'URL
  const urlParts = fileUrl.split('/');
  const fileName = urlParts[urlParts.length - 1].split('?')[0];

  const { error } = await supabase.storage
    .from('psy_practitioner_docs')
    .remove([fileName]);

  if (error) throw error;
};
